import React from 'react';
import PropTypes from 'prop-types';
import {observer} from 'mobx-react';
import {Card} from 'components/card';
import _PriceRange from './components/PriceRange';
import _Stops from './components/Stops';
import _Transportations from './components/Transportations';
import _Providers from './components/Providers';
import _Airlines from './components/Airlines';
import _FlightTimes from './components/FlightTimes';
import Counter from '../results/Counter';
import EventTrigger from 'helpers/EventTrigger';

const PriceRange = observer(_PriceRange);
const Stops = observer(_Stops);
const Transportations = observer(_Transportations);
const Providers = observer(_Providers);
const Airlines = observer(_Airlines);
const FlightTimes = observer(_FlightTimes);

const toggleSideBar = () => {
  const list = document.body.classList;
  if (list.contains('show-sidebar')) {
    list.remove('show-sidebar');
  } else {
    list.add('show-sidebar');
  }
};

const Filters = ({store, isRoundtrip, resultsCounts, currency, translations}) => {
  const {total, remaining} = resultsCounts;
  const {resultsTitle, filters, showAllResults} = translations;
  return (
    <div id="sidebar" className="grid-item-lg-3-10">
      <div id="sidebarButton" onClick={toggleSideBar}>
        <div><i className="icon icon-setup-preferences" />
          {filters}&nbsp;(
          <Counter
            resultsTitle={resultsTitle}
            remain={remaining}
            total={total}
            sidebar
          />
          )</div>
        <i className="icon icon-up-arrow icon-lg" aria-hidden="true" />
      </div>
      <div id="tabContent" className="sidebar-block">
        <div id="filters">
          <Card>
            <div className="list-group list-group-flush">
              <PriceRange
                price={store.price}
                updatePriceValue={store.updatePriceValue}
                resetPriceValue={store.resetPriceValue}
                currency={currency}
                translations={translations}
                onPriceChange={store.onPriceChange}
              />
              <Stops
                stops={store.stops}
                selectStopsValue={store.selectStopsValue}
                currency={currency}
                translations={translations}
              />
              {store.transportTypes.options.length > 1 &&
                <Transportations
                  allTransportationsAreChecked={store.allTransportationsAreChecked}
                  transportTypes={store.transportTypes}
                  toggleTransportation={store.toggleTransportation}
                  selectSingleTransportation={store.selectSingleTransportation}
                  toggleAllTransportations={store.toggleAllTransportations}
                  currency={currency}
                  translations={translations}
                />
              }
              <Airlines
                allAirlinesAreChecked={store.allAirlinesAreChecked}
                airlines={store.airlines}
                toggleAirline={store.toggleAirline}
                selectSingleAirline={store.selectSingleAirline}
                toggleAirlinesWithCombinations={store.toggleAirlinesWithCombinations}
                toggleAllAirlines={store.toggleAllAirlines}
                currency={currency}
                translations={translations}
              />
              {store.withProviders &&
                <Providers
                  allProvidersAreChecked={store.allProvidersAreChecked}
                  providers={store.providers}
                  toggleProvider={store.toggleProvider}
                  selectSingleProvider={store.selectSingleProvider}
                  toggleAllProviders={store.toggleAllProviders}
                  currency={currency}
                  translations={translations}
                />
              }
              <FlightTimes
                times={store.obTimes}
                type="departure"
                translations={translations}
                updateFlightTimeValue={store.updateFlightTimeValue}
                resetFlightTimeValue={store.resetFlightTimeValue}
                onTimeChange={store.onDepartureTimeChange}
                onDurationChange={store.onDepartureDurationChange}
              />
              {
                isRoundtrip &&
                  <FlightTimes
                    times={store.ibTimes}
                    type="arrival"
                    translations={translations}
                    updateFlightTimeValue={store.updateFlightTimeValue}
                    resetFlightTimeValue={store.resetFlightTimeValue}
                    onTimeChange={store.onReturnTimeChange}
                    onDurationChange={store.onReturnDurationChange}
                  />
              }
              <div className="list-group-item border-bottom-0">
                <button onClick={store.resetAllFilters} className="btn btn-default btn-block text-truncate">
                  {showAllResults}
                </button>
              </div>
            </div>
            <div className="text-center">
              <EventTrigger id="filters-ad" event="filtersAdRendered"/>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

Filters.propTypes = {
  currency: PropTypes.object.isRequired,
  isRoundtrip: PropTypes.bool.isRequired,
  resultsCounts: PropTypes.shape({
    total: PropTypes.number,
    remaining: PropTypes.number
  }).isRequired,
  store: PropTypes.shape({
    airlines: PropTypes.shape({
      name: PropTypes.string,
      price: PropTypes.number,
      code: PropTypes.string
    }),
    providers: PropTypes.shape({
      name: PropTypes.string,
      price: PropTypes.number
    }),
    transportTypes: PropTypes.shape({
      options: PropTypes.object,
      name: PropTypes.string,
      price: PropTypes.number
    }),
    price: PropTypes.shape({
      defaultValue: PropTypes.number,
      value: PropTypes.number,
      min: PropTypes.number,
      max: PropTypes.number
    }),
    baggages: PropTypes.shape({
      name: PropTypes.string,
      disabled: PropTypes.bool
    }),
    stops: PropTypes.shape({
      defaultValue: PropTypes.number.isRequired,
      value: PropTypes.number,
      options: PropTypes.object.isRequired
    }),
    times: PropTypes.object,
    obTimes: PropTypes.object.isRequired,
    ibTimes: PropTypes.object.isRequired,
    allAirlinesAreChecked: PropTypes.bool.isRequired,
    selectSingleAirline: PropTypes.func.isRequired,
    updatePriceValue: PropTypes.func.isRequired,
    resetPriceValue: PropTypes.func.isRequired,
    selectStopsValue: PropTypes.func.isRequired,
    toggleAllAirlines: PropTypes.func.isRequired,
    toggleAirline: PropTypes.func.isRequired,
    toggleAirlinesWithCombinations: PropTypes.func.isRequired,
    resetAllFilters: PropTypes.func.isRequired,
    onPriceChange: PropTypes.func.isRequired,
    selectSingleProvider: PropTypes.func.isRequired,
    toggleAllProviders: PropTypes.func.isRequired,
    allProvidersAreChecked: PropTypes.bool.isRequired,
    toggleProvider: PropTypes.func.isRequired,
    allTransportationsAreChecked: PropTypes.bool.isRequired,
    selectSingleTransportation: PropTypes.func.isRequired,
    toggleAllTransportations: PropTypes.func.isRequired,
    toggleTransportation: PropTypes.func.isRequired,
    onReturnDurationChange: PropTypes.func.isRequired,
    onReturnTimeChange: PropTypes.func.isRequired,
    resetFlightTimeValue: PropTypes.func.isRequired,
    updateFlightTimeValue: PropTypes.func.isRequired,
    onDepartureTimeChange: PropTypes.func.isRequired,
    onDepartureDurationChange: PropTypes.func.isRequired,
    withProviders: PropTypes.bool.isRequired
  }),
  translations: PropTypes.shape({
    showAllResults: PropTypes.string.isRequired,
    resultsTitle: PropTypes.string.isRequired,
    filters: PropTypes.string.isRequired,
    priceRange: PropTypes.string.isRequired,
    maxPrice: PropTypes.string.isRequired,
    reset: PropTypes.string.isRequired
  })
};

export default observer(Filters);
